import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useContext
} from 'react';
import useNotificationsBadge from 'src/utils/Hooks/useNotificationsBadge';
import {
  findProgressItems,
  ExportDownloadItem
} from 'src/components/BeaconV2Components/common/AppExportModal/exportDataUtils';
import { POLL_STATE, fetchProgressItemsAndReplace, updateDownloadList, initializeDownloadList } from './ExportUtils';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';

export interface ExportContextValue {
  startPolling: () => void;
  isFailed: boolean;
  pollState: POLL_STATE;
  downloadList: ExportDownloadItem[];
  setDownloadList: Dispatch<SetStateAction<ExportDownloadItem[]>>;
  isDownloadLoading: boolean;
  setIsDownloadLoading: Dispatch<SetStateAction<boolean>>;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  setPollState: Dispatch<SetStateAction<POLL_STATE>>;
  stopPolling: () => void;
}

export const ExportContext = createContext<ExportContextValue>(null);

export function useExportContext() {
  const context = useContext(ExportContext);
  return context;
}

export const EXPORT_DOWNLOAD_PAGE_SIZE = 50;

export * from './ExportUtils';

/**
 * This Context holds export related functions and state
 */
const ExportContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [pollState, setPollState] = useState<POLL_STATE>(POLL_STATE.STOPPED);
  const [downloadList, setDownloadList] = useState<ExportDownloadItem[]>(undefined);
  const [isDownloadLoading, setIsDownloadLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const status = useAppSelector((state) => state.user?.session?.status);

  const startPolling = () => {
    initializeDownloadList(setIsDownloadLoading, setDownloadList);
  };

  useEffect(() => {
    updateDownloadList(downloadList);
  }, [downloadList]);

  const isFailed = pollState === POLL_STATE.ERROR;

  const memoizedExportValues = useMemo(() => {
    return {
      startPolling,
      stopPolling: () => setPollState(POLL_STATE.STOPPED),
      isFailed,
      pollState,
      downloadList,
      setDownloadList,
      isDownloadLoading,
      setIsDownloadLoading,
      currentPage,
      setCurrentPage,
      setPollState
    };
  }, [currentPage, downloadList, isDownloadLoading, isFailed, pollState]);
  const { setExportBadge } = useNotificationsBadge();
  const { showSnackbar } = useSnackbar();

  const fetch = useCallback(() => {
    fetchProgressItemsAndReplace(setDownloadList, setPollState, setExportBadge, showSnackbar);
  }, [setExportBadge, showSnackbar]);

  useEffect(() => {
    const fetchDownloadPageTimer: NodeJS.Timeout | null = null;
    try {
      if (downloadList?.length > 0) {
        const shouldPollForDownload = findProgressItems(downloadList)?.length > 0;
        if (shouldPollForDownload && pollState !== POLL_STATE.RUNNING) {
          fetch();
        }
      } else if (!downloadList && status && !(pollState === POLL_STATE.RUNNING)) {
        initializeDownloadList(setIsDownloadLoading, setDownloadList);
      }
    } catch (error) {
      console.error('Error while fetching download page:', error);
    }

    return () => {
      if (fetchDownloadPageTimer) {
        clearTimeout(fetchDownloadPageTimer);
      }
    };
  }, [downloadList, fetch, pollState, setDownloadList, setIsDownloadLoading, status]);

  return <ExportContext.Provider value={memoizedExportValues}>{children}</ExportContext.Provider>;
};

export default ExportContextProvider;
